import "./ProjectCard.css";
import "./ProjectCardKeyData.css";

import { MouseEventHandler } from "react";
import { NumericFormat } from "react-number-format";

import { PriceTagOrangeIcon, UnitsGreenIcon, VerifiedBlueIcon } from "../../../assets";
import { StandardConstants } from "../../../constants";
import { BadgeData, FileType } from "../../../models";
import { getPlaceholderImage } from "../../../utils";
import { LocationFlag, Tag } from "../../data";
import { Divider } from "../../general";
import { Pill } from "../../wayfinding";

interface ProjectCardProps {
  imageSource: string | null;
  hasUnits?: boolean;
  hasPriceInDirectory?: boolean;
  seekingFunding?: boolean;
  name: string;
  addressCountryCode: string;
  developerSmallLogoFileUrl: string | null;
  developerDisplayName?: string;
  standardDisplayName?: string;
  listingContentShortDescription: string;
  tags?: string[];
  badges?: BadgeData[];
  certifications?: BadgeData[];
  calculatedIssuancePriceMin: number | null;
  calculatedIssuancePriceMax: number | null;
  cachedTotalQuantity: number | null;
  className?: string;
  onClick?: MouseEventHandler<HTMLDivElement>;
  quantityType: string | null;
}

// cachedQuantityType holds every quantity type a project has issued, joined with " + ".
const getFormattedUnitTypes = (quantityType: string | null): string =>
  (quantityType ?? "")
    .split(" + ")
    .map((unitType) => unitType.trim())
    .filter(Boolean)
    .join(", ");

const getFormattedUnitPrice = (
  calculatedIssuancePriceMin: number | null,
  calculatedIssuancePriceMax: number | null,
  hasPriceInDirectory: boolean,
  hasUnitQuantity: boolean
): JSX.Element | string => {
  // Units with no price on public display are priced on application, whether the developer
  // hid the price or never entered one.
  if (!hasPriceInDirectory || (calculatedIssuancePriceMin === null && calculatedIssuancePriceMax === null)) {
    return hasUnitQuantity ? "POA" : "-";
  }
  if (calculatedIssuancePriceMax?.toFixed(2) === calculatedIssuancePriceMin?.toFixed(2)) {
    return (
      <NumericFormat
        value={calculatedIssuancePriceMin}
        displayType="text"
        thousandSeparator
        decimalScale={2}
        fixedDecimalScale
        prefix="£"
      />
    );
  }
  if (calculatedIssuancePriceMin !== null && calculatedIssuancePriceMax !== null) {
    return (
      <>
        <NumericFormat
          value={calculatedIssuancePriceMin}
          displayType="text"
          thousandSeparator
          decimalScale={2}
          fixedDecimalScale
          prefix="£"
        />
        &nbsp; - &nbsp;
        <NumericFormat
          value={calculatedIssuancePriceMax}
          displayType="text"
          thousandSeparator
          decimalScale={2}
          fixedDecimalScale
          prefix="£"
        />
      </>
    );
  }
  if (calculatedIssuancePriceMin !== null) {
    return (
      <NumericFormat
        value={calculatedIssuancePriceMin}
        displayType="text"
        thousandSeparator
        decimalScale={2}
        fixedDecimalScale
        prefix="£"
      />
    );
  }
  if (calculatedIssuancePriceMax !== null) {
    return (
      <NumericFormat
        value={calculatedIssuancePriceMax}
        displayType="text"
        thousandSeparator
        decimalScale={2}
        fixedDecimalScale
        prefix="£"
      />
    );
  }
  return "-";
};

export const ProjectCard = ({
  imageSource,
  hasUnits = true,
  hasPriceInDirectory = true,
  seekingFunding = false,
  name,
  addressCountryCode,
  developerSmallLogoFileUrl,
  developerDisplayName,
  standardDisplayName,
  listingContentShortDescription,
  tags,
  badges,
  certifications,
  calculatedIssuancePriceMin,
  calculatedIssuancePriceMax,
  cachedTotalQuantity,
  className,
  onClick,
  quantityType,
}: ProjectCardProps): JSX.Element => {
  return (
    <div className={`ProjectCard ${className || ""}`} onClick={onClick} aria-hidden="true">
      <div
        style={{
          backgroundImage: imageSource ? `url(${imageSource})` : `url(${getPlaceholderImage(standardDisplayName)})`,
        }}
        className="ProjectCardImageBlock"
      >
        {hasUnits && <span className="body2">Units available</span>}
        {seekingFunding && <span className="body2 SeekingFunding">Seeking funding</span>}
      </div>
      {((!!badges && !!badges.length) || (!!certifications && !!certifications.length)) && (
        <div className="ProjectCardBadges">
          {[...(badges ?? []), ...(certifications ?? [])].map((b) => (
            <img key={b.uuid} alt={b.displayName} src={b.files.find((f) => f.type === FileType.SmallLogo)?.file.url} />
          ))}
        </div>
      )}
      <div className="ProjectCardContent">
        <div className="ProjectCardDetails">
          <div className="ProjectCardDetailsHeader">
            <h2>{name}</h2>
            {addressCountryCode && <LocationFlag addressCountryCode={addressCountryCode} />}
          </div>
          <div className="ProjectCardDetailsDeveloper">
            {developerSmallLogoFileUrl && <img src={developerSmallLogoFileUrl} alt="DeveloperLogo" />}
            <span className="body2">{developerDisplayName}</span>
            {standardDisplayName && (
              <Pill label={standardDisplayName} variantMap={StandardConstants.CODE_PILL_VARIANT_MAP} />
            )}
          </div>
          <span className="body2">{listingContentShortDescription}</span>
          <div className="ProjectCardDetailsTags">{tags?.map((tagName) => <Tag key={tagName} label={tagName} />)}</div>
        </div>
        <div className="VerticalDivider">
          <Divider type="vertical" />
        </div>
        <div className="HorizontalDivider">
          <Divider type="horizontal" />
        </div>
        <div className="ProjectCardKeyDataContainer">
          <div className="ProjectCardKeyData ProjectCardKeyDataPrice">
            <div className="ProjectCardKeyDataLabel">
              <PriceTagOrangeIcon />
              <h4>Price</h4>
            </div>
            <span className="body4">
              {getFormattedUnitPrice(
                calculatedIssuancePriceMin,
                calculatedIssuancePriceMax,
                hasPriceInDirectory,
                (cachedTotalQuantity ?? 0) > 0
              )}
            </span>
          </div>
          <div className="ProjectCardKeyData ProjectCardKeyDataNumber">
            <div className="ProjectCardKeyDataLabel">
              <UnitsGreenIcon />
              <h4>Units</h4>
            </div>
            <span className="body4">
              {cachedTotalQuantity ? (
                <NumericFormat value={cachedTotalQuantity} displayType="text" thousandSeparator />
              ) : (
                "-"
              )}
            </span>
          </div>
          <div className="ProjectCardKeyData ProjectCardKeyDataType">
            <div className="ProjectCardKeyDataLabel">
              <VerifiedBlueIcon />
              <h4>Type</h4>
            </div>
            <span className="body4 ProjectCardKeyDataTypeValue" title={getFormattedUnitTypes(quantityType)}>
              {getFormattedUnitTypes(quantityType)}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};
